/**
 * Users API
 * Handles API calls for fetching user data
 *
 * @endpoint POST /api/sodms/users
 * @param {GetUsersRequest} payload - Request parameters
 * @param {boolean} payload.includeDetails - Include detailed user information
 * @param {number[]} payload.userKeys - Array of user IDs to fetch
 * @param {string} payload.columnToSort - Column name for sorting
 * @param {string} payload.globalSearch - Search text to find
 * @param {number} payload.max - Maximum number of records to return
 * @param {number} payload.offset - Offset for pagination
 * @param {string} payload.order - Sort order (ASC/DESC)
 *
 * @returns {Object} Response object
 * @returns {UserItem[]} returns.data - Array of user items with properties like userId, username, firstName, etc.
 * @returns {number} returns.totalRecords - Total number of records available
 */
import { createApi } from '@reduxjs/toolkit/query/react';

import { axiosBaseQuery } from '../../../../../../shared/services/baseQuery';
import { detailsActions } from '../../../../data-store/detailsSlice';
import {
  setUsersData,
  setUsersIsLoading,
  setUsersTotalRecords,
} from '../data-store';

import {
  type GetUsersApiResponse,
  type GetUsersRequest,
  type UserItem,
  UsersType,
} from './usersTypes';

export const usersApi = createApi({
  reducerPath: UsersType.API_SLICE_NAME,
  baseQuery: axiosBaseQuery({}),
  keepUnusedDataFor: 60, // Cache for 1 minute
  endpoints: (builder) => ({
    getUsers: builder.mutation<
      { data: UserItem[]; totalRecords: number },
      GetUsersRequest
    >({
      query: (payload: GetUsersRequest) => ({
        url: '/api/sodms/users',
        method: 'POST',
        data: payload,
        isECMv6: true,
      }),
      transformResponse: (response: GetUsersApiResponse) => {
        if (response.status === 200) {
          return {
            data: response.data,
            totalRecords: response.totalRecords,
          };
        }

        return { data: [], totalRecords: 0 };
      },
      onQueryStarted: (payload, { dispatch }) => {
        const isSidePanelRequest =
          payload.max === 25 && payload.userKeys?.length === 1;

        if (!isSidePanelRequest) {
          dispatch(setUsersIsLoading(true));
          dispatch(detailsActions.setEntitlementUsersLoading(true));
        }
      },
      onCacheEntryAdded: async (
        payload,
        { dispatch, cacheDataLoaded, cacheEntryRemoved }
      ) => {
        const isSidePanelRequest =
          payload.max === 25 && payload.userKeys?.length === 1;

        try {
          const result = await cacheDataLoaded;

          // Only update main list state if this is not a single item request (sidepanel)
          if (!isSidePanelRequest) {
            dispatch(setUsersData(result.data.data));
            dispatch(setUsersTotalRecords(result.data.totalRecords));
            dispatch(setUsersIsLoading(false));
            dispatch(detailsActions.setEntitlementUsersData(result.data.data));
            dispatch(
              detailsActions.setEntitlementUsersTotalRecords(
                result.data.totalRecords
              )
            );
          }
        } catch (error) {
          if (!isSidePanelRequest) {
            dispatch(setUsersIsLoading(false));
            dispatch(
              detailsActions.setEntitlementUsersError(
                error instanceof Error ? error.message : 'Unknown error'
              )
            );
          }
        }

        await cacheEntryRemoved;
      },
    }),
  }),
});

export const { useGetUsersMutation } = usersApi;
